// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../reactive-store.js'

export class EditControls extends HTMLElement {
  /** @type {string | null} */
  #previousGeometryState = null

  connectedCallback () {
    this.#setupClickHandlers()

    // only show edit options relevant to selected feature
    reactiveStore.selectedFeatureId.subscribe((/** @type string */ id) => {
      // No feature selected - hide all tools
      if (!id) {
        this.#filterOptionsByType('none')
        this.#selectAvailableMode()
        this.#previousGeometryState = null
        return
      }

      const type = this.#resolveFeatureType(id)
      this.#filterOptionsByType(type)
      this.#selectAvailableMode()

      this.#previousGeometryState = type
    })

    // Update checked radio button when edit mode changes externally
    reactiveStore.editMode.subscribe((/** @type string */ mode) => {
      /** @type {HTMLInputElement | null} */
      const button = this.querySelector(`input[value="${mode}"]`)
      if (button && !button.checked) {
        button.checked = true
      }
    })

    // Update tools when geometry state changes via undo/redo or cut
    reactiveStore.features.subscribe((/** @type {Array} */ features, /** @type {string} */ message) => {
      if (message && message !== 'undo-redo') return
      const selectedId = reactiveStore.selectedFeatureId.get()
      if (!selectedId) {
        this.#previousGeometryState = null
        return
      }
      const type = this.#resolveFeatureType(selectedId)
      if (type === this.#previousGeometryState) return
      this.#previousGeometryState = type
      this.#filterOptionsByType(type)
      this.#selectAvailableMode()
    })
  }

  /**
   * Selects a feature when clicked
   */
  #setupClickHandlers () {
    /** @type {NodeListOf<HTMLInputElement>} */
    const buttons = this.querySelectorAll('input[type="radio"]')
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        reactiveStore.editMode.set(button.value)
      })
    })
  }

  /**
   * Only show edit options relevant to the selected feature type
   * @param {string} type
   */
  #filterOptionsByType (type) {
    // Normalise MultiPolygon to Polygon for tool matching
    if (type === 'MultiPolygon') {
      type = 'Polygon'
    }
    // Normalise MultiPoint to Point for tool matching
    if (type === 'MultiPoint') {
      type = 'Point'
    }
    /** @type {NodeListOf<HTMLElement} */
    const options = this.querySelectorAll('[data-for]')
    options.forEach((option) => {
      const allowedTypes = (option.dataset.for || '').toLowerCase().split(/\s+/)
      option.classList.toggle(
        'govuk-!-display-none',
        !allowedTypes.includes(type.toLowerCase())
      )
    })
  }

  /**
   * Resolve the tool-filter type for a given feature id
   * @param {string} id
   * @returns {string}
   */
  #resolveFeatureType (id) {
    let type = 'none'
    reactiveStore.features.get().forEach((feature) => {
      if (feature.properties._id === id) {
        if (feature.geometry && feature.geometry.coordinates.length) {
          type = feature.properties?._isMapOverlay ? 'overlay' : feature.geometry.type
        } else {
          type = feature.properties?.feature_type === 'tree' ? 'null-geometry-point' : 'null-geometry-polygon'
        }
      }
    })
    return type
  }

  /**
   * Selects the first available edit mode from the visible options
   */
  #selectAvailableMode () {
    // Try to preserve the current mode if it's still available
    const currentMode = reactiveStore.editMode.get()
    const currentModeOption = this.querySelector(`input[value="${currentMode}"]`)
    if (currentModeOption) {
      const parentOption = currentModeOption.closest('[data-for]')
      if (parentOption && !parentOption.classList.contains('govuk-!-display-none')) {
        // Current mode is still available - keep it
        return
      }
    }

    // Current mode not available - select the first visible one
    for (const option of this.querySelectorAll('[data-for]')) {
      if (option.classList.contains('govuk-!-display-none')) continue
      /** @type {HTMLInputElement | null} */
      const input = option.querySelector('input')
      if (input) {
        input.checked = true
        reactiveStore.editMode.set(input.value)
        return
      }
    }
  }

  /**
   * Re-filters edit options for the currently selected feature
   * without resetting the edit mode.
   */
  refreshOptionsForCurrentFeature () {
    const id = reactiveStore.selectedFeatureId.get()
    if (!id) {
      this.#filterOptionsByType('none')
      return
    }

    const type = this.#resolveFeatureType(id)
    this.#filterOptionsByType(type)
  }
}

customElements.define('edit-controls', EditControls)
